import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Button, Tabs, Tab,
  CircularProgress, Snackbar, Alert, LinearProgress, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { coursesAPI } from '../services/api';
import AudioPlayer from '../components/audio/AudioPlayer'; // Import the AudioPlayer component

// Import components
import CourseHeader from '../components/courses/CourseHeader';
import CourseDetailsPanel from '../components/courses/CourseDetailsPanel';
import ThemesPanel from '../components/courses/ThemesPanel';
import BlocksPanel from '../components/courses/BlocksPanel';
import CourseFormDialog from '../components/courses/dialogs/CourseFormDialog';
import ThemeFormDialog from '../components/courses/dialogs/ThemeFormDialog';
import BlockFormDialog from '../components/courses/dialogs/BlockFormDialog';
import ContentGenerationDialog from '../components/courses/dialogs/ContentGenerationDialog';
import AudioGenerationDialog from '../components/books/AudioGenerationDialog'; // Update this import

export default function CourseDetail() {
  const { courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // State
  const [course, setCourse] = useState(null);
  const [themes, setThemes] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [activeThemeId, setActiveThemeId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [editingBlock, setEditingBlock] = useState(null);
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingLevel, setGeneratingLevel] = useState(null); // Add state to track progress when generating all levels
  
  // Notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Add state to track block statistics for all themes
  const [themeStats, setThemeStats] = useState({});

  // Add state for cover generation
  const [generatingCovers, setGeneratingCovers] = useState(false);

  // Add state for audio generation
  const [audioDialogOpen, setAudioDialogOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);

  // Add state for bulk audio generation
  const [bulkAudioDialogOpen, setBulkAudioDialogOpen] = useState(false);
  const [bulkAudioProgress, setBulkAudioProgress] = useState(0);
  const [totalBlocksForAudio, setTotalBlocksForAudio] = useState(0);
  const [isGeneratingBulkAudio, setIsGeneratingBulkAudio] = useState(false);

  // Helper function to count words (same as in BlocksPanel)
  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  // Check if we should open the edit dialog on load
  useEffect(() => {
    if (location.state?.openEditDialog) {
      setCourseDialogOpen(true);
    }
  }, [location.state]);

  // Fetch course details
  useEffect(() => {
    const fetchCourseDetails = async () => {
      setLoading(true);
      try {
        const response = await coursesAPI.getCourseById(courseId);
        setCourse(response.data);
        
        // Fetch themes for the course
        const themesResponse = await coursesAPI.getThemesForCourse(courseId);
        const sortedThemes = Array.isArray(themesResponse.data) 
          ? themesResponse.data.sort((a, b) => a.order - b.order)
          : [];
        setThemes(sortedThemes);
        
        if (sortedThemes.length > 0) {
          setActiveThemeId(sortedThemes[0].id);
        }
      } catch (error) {
        console.error('Error fetching course details:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load course details',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourseDetails();
  }, [courseId]);

  // Fetch statistics for all themes
  useEffect(() => {
    if (themes.length > 0) {
      themes.forEach(theme => {
        fetchThemeStats(theme.id);
      });
    }
  }, [themes]);

  // Update statistics for current theme when blocks change
  useEffect(() => {
    if (activeThemeId && blocks.length >= 0) {
      // Group blocks by level
      const blocksByLevel = blocks.reduce((acc, block) => {
        const level = block.level;
        if (!acc[level]) acc[level] = [];
        acc[level].push(block);
        return acc;
      }, {});
      
      // Calculate statistics
      const levelStats = {};
      for (const level in blocksByLevel) {
        const blocksInLevel = blocksByLevel[level];
        const wordCount = blocksInLevel.reduce(
          (sum, block) => sum + countWords(block.content), 0
        );
        levelStats[level] = {
          blocks: blocksInLevel.length,
          words: wordCount
        };
      }
      
      // Update stats for the active theme
      setThemeStats(prev => ({
        ...prev,
        [activeThemeId]: levelStats
      }));
    }
  }, [blocks, activeThemeId]);

  // Fetch block statistics for a theme
  const fetchThemeStats = async (themeId) => {
    try {
      const response = await coursesAPI.getBlocksForTheme(themeId);
      if (!response.data) return;
      
      const themeBlocks = Array.isArray(response.data) ? response.data : [];
      
      // Group blocks by level
      const blocksByLevel = themeBlocks.reduce((acc, block) => {
        const level = block.level;
        if (!acc[level]) acc[level] = [];
        acc[level].push(block);
        return acc;
      }, {});
      
      // Calculate statistics for each level
      const levelStats = {};
      for (const level in blocksByLevel) {
        const blocksInLevel = blocksByLevel[level];
        const wordCount = blocksInLevel.reduce(
          (sum, block) => sum + countWords(block.content), 0
        );
        levelStats[level] = {
          blocks: blocksInLevel.length,
          words: wordCount
        };
      }
      
      // Update the theme stats
      setThemeStats(prev => ({
        ...prev,
        [themeId]: levelStats
      }));
    } catch (error) {
      console.error(`Error fetching stats for theme ${themeId}:`, error);
    }
  };

  // Fetch blocks when activeThemeId changes
  useEffect(() => {
    if (activeThemeId) {
      const fetchBlocks = async () => {
        try {
          const response = await coursesAPI.getBlocksForTheme(activeThemeId);
          const blocksData = Array.isArray(response.data) 
            ? response.data.sort((a, b) => a.order - b.order) 
            : [];
          setBlocks(blocksData);
        } catch (error) {
          console.error('Error fetching blocks:', error);
          setSnackbar({
            open: true,
            message: 'Failed to load blocks for this theme',
            severity: 'error'
          });
        }
      };
      
      fetchBlocks();
    }
  }, [activeThemeId]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Course management
  const handleCourseDialogOpen = () => {
    setCourseDialogOpen(true);
  };

  const handleCourseDialogClose = () => {
    setCourseDialogOpen(false);
  };

  const handleCourseSubmit = async (values, { setSubmitting }) => {
    try {
      await coursesAPI.updateCourse(courseId, values);
      setCourse({ ...course, ...values });
      setSnackbar({
        open: true,
        message: 'Course updated successfully',
        severity: 'success'
      });
      handleCourseDialogClose();
    } catch (error) {
      console.error('Error updating course:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update course',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Update the handler for cover generation to use the correct parameter format
  const handleGenerateCover = async (type) => {
    if (!courseId) return;
    
    setGeneratingCovers(true);
    try {
      await coursesAPI.generateCourseCovers(courseId, {
        prompt: " ", // Empty prompt or could provide a default description based on course details
        coverType: type // This parameter should be 'vertical', 'horizontal', or 'both'
      });
      
      // Refresh course data to get new cover URLs
      const response = await coursesAPI.getCourseById(courseId);
      setCourse(response.data);
      
      setSnackbar({
        open: true,
        message: `Successfully generated ${type} course cover(s)`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error generating course covers:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate course covers',
        severity: 'error'
      });
    } finally {
      setGeneratingCovers(false);
    }
  };

  // Theme management
  const handleOpenThemeDialog = (theme = null) => {
    setEditingTheme(theme);
    setThemeDialogOpen(true);
  };

  const handleCloseThemeDialog = () => {
    setThemeDialogOpen(false);
    setEditingTheme(null);
  };

  const handleThemeSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingTheme) {
        await coursesAPI.updateTheme(courseId, editingTheme.id, values);
        setSnackbar({
          open: true,
          message: 'Theme updated successfully',
          severity: 'success'
        });
        
        // Update themes list
        setThemes(themes.map(t => 
          t.id === editingTheme.id ? { ...t, ...values } : t
        ));
      } else {
        const response = await coursesAPI.createTheme(courseId, values);
        setSnackbar({
          open: true,
          message: 'Theme created successfully',
          severity: 'success'
        });
        
        // Add new theme to list
        if (response && response.data) {
          setThemes([...themes, response.data]);
        } else {
          // If response doesn't have expected structure, refresh the themes list
          const themesResponse = await coursesAPI.getThemesForCourse(courseId);
          setThemes(Array.isArray(themesResponse.data) 
            ? themesResponse.data.sort((a, b) => a.order - b.order)
            : []);
        }
      }
      handleCloseThemeDialog();
      resetForm();
    } catch (error) {
      console.error('Error saving theme:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${editingTheme ? 'update' : 'create'} theme`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTheme = async (themeId) => {
    if (!window.confirm('Are you sure you want to delete this theme? All blocks will be deleted as well.')) {
      return;
    }
    
    try {
      await coursesAPI.deleteTheme(courseId, themeId);
      setSnackbar({
        open: true,
        message: 'Theme deleted successfully',
        severity: 'success'
      });
      
      // Remove theme from list and select a new active theme if needed
      const updatedThemes = themes.filter(t => t.id !== themeId);
      setThemes(updatedThemes);
      
      if (activeThemeId === themeId && updatedThemes.length > 0) {
        setActiveThemeId(updatedThemes[0].id);
      } else if (updatedThemes.length === 0) {
        setActiveThemeId(null);
        setBlocks([]);
      }
    } catch (error) {
      console.error('Error deleting theme:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete theme',
        severity: 'error'
      });
    }
  };

  const handleThemeReorder = async (result) => {
    if (!result.destination) return;
    
    const reorderedThemes = Array.from(themes);
    const [removed] = reorderedThemes.splice(result.source.index, 1);
    reorderedThemes.splice(result.destination.index, 0, removed);
    
    // Update UI immediately with optimistic update
    setThemes(reorderedThemes);
    
    try {
      // Save new order to backend
      await coursesAPI.reorderThemes(courseId, {
        themeIds: reorderedThemes.map(theme => theme.id)
      });
      setSnackbar({
        open: true,
        message: 'Themes reordered successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error reordering themes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reorder themes',
        severity: 'error'
      });
    }
  };

  // Add a function to handle generating content for a specific theme
  const handleThemeContentGeneration = (themeId) => {
    // Set the active theme
    setActiveThemeId(themeId);
    
    // Switch to the blocks tab
    setActiveTab(1);
    
    // Open the generation dialog
    setGenerationDialogOpen(true);
  };

  // Add a combined handler for selecting a theme and viewing its blocks
  const handleViewThemeBlocks = (themeId) => {
    setActiveThemeId(themeId);
    setActiveTab(1); // Switch to the blocks tab
  };

  // Block management
  const handleOpenBlockDialog = (block = null) => {
    setEditingBlock(block);
    setBlockDialogOpen(true);
  };

  const handleCloseBlockDialog = () => {
    setBlockDialogOpen(false);
    setEditingBlock(null);
  };

  const handleBlockSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingBlock) {
        await coursesAPI.updateBlock(activeThemeId, editingBlock.id, values);
        setSnackbar({
          open: true,
          message: 'Block updated successfully',
          severity: 'success'
        });
        
        // Update blocks list
        setBlocks(blocks.map(b => 
          b.id === editingBlock.id ? { ...b, ...values } : b
        ));
      } else {
        const response = await coursesAPI.createBlock(activeThemeId, {
          ...values,
          order: blocks.filter(b => b.level === values.level).length // Add at the end of blocks with the same level
        });
        setSnackbar({
          open: true,
          message: 'Block created successfully',
          severity: 'success'
        });
        
        // Add new block to list
        if (response && response.data) {
          setBlocks([...blocks, response.data]);
        } else {
          // If response doesn't have expected structure, refresh the blocks list
          const blocksResponse = await coursesAPI.getBlocksForTheme(activeThemeId);
          setBlocks(Array.isArray(blocksResponse.data) 
            ? blocksResponse.data.sort((a, b) => a.order - b.order)
            : []);
        }
      }
      handleCloseBlockDialog();
      resetForm();
    } catch (error) {
      console.error('Error saving block:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${editingBlock ? 'update' : 'create'} block`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Are you sure you want to delete this block?')) {
      return;
    }
    
    try {
      await coursesAPI.deleteBlock(activeThemeId, blockId);
      setSnackbar({
        open: true,
        message: 'Block deleted successfully',
        severity: 'success'
      });
      
      // Remove block from list
      setBlocks(blocks.filter(b => b.id !== blockId));
    } catch (error) {
      console.error('Error deleting block:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete block',
        severity: 'error'
      });
    }
  };

  const handleBlockReorder = async (result) => {
    if (!result.destination) return;
    
    // Find the level of blocks being reordered based on droppableId
    const level = parseInt(result.draggableId.split('-')[2], 10); 
    
    // Only reorder blocks within the same level
    const blocksOfLevel = blocks.filter(b => b.level === level);
    const otherBlocks = blocks.filter(b => b.level !== level);
    
    // Apply reorder within level
    const reorderedBlocks = Array.from(blocksOfLevel);
    const [removed] = reorderedBlocks.splice(result.source.index, 1);
    reorderedBlocks.splice(result.destination.index, 0, removed);
    
    // Update UI immediately
    setBlocks([...otherBlocks, ...reorderedBlocks]);
    
    try {
      // Save new order to backend
      await coursesAPI.reorderBlocks(activeThemeId, {
        blockIds: reorderedBlocks.map(block => block.id),
        level
      });
      setSnackbar({
        open: true,
        message: 'Blocks reordered successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error reordering blocks:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reorder blocks',
        severity: 'error'
      });
    }
  };

  const handleOpenGenerationDialog = () => {
    setGenerationDialogOpen(true);
  };

  const handleCloseGenerationDialog = () => {
    setGenerationDialogOpen(false);
  };

  const handleGenerateContent = async (values, { setSubmitting }) => {
    if (!activeThemeId) return;
    
    setIsGenerating(true);
    
    try {
      // Check if we're generating for all levels
      if (values.level === 'all') {
        // Define the levels we want to generate
        const levelsToGenerate = [1, 2, 3];
        let allGeneratedBlocks = 0;
        
        // Generate content for each level sequentially
        for (const level of levelsToGenerate) {
          setGeneratingLevel(level);
          
          const generationData = {
            courseId: courseId,
            themeId: activeThemeId,
            level,
            model: values.model,
            customPrompt: values.additionalPrompt || undefined
          };
          
          const response = await coursesAPI.generateThemeBlocks(generationData);
          allGeneratedBlocks += (response.data?.blocks?.length || 0);
          
          // Small delay between requests to avoid rate limits
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        setGeneratingLevel(null);
        
        setSnackbar({
          open: true,
          message: `Successfully generated ${allGeneratedBlocks} content blocks across all levels`,
          severity: 'success'
        });
      } else {
        // Original logic for single level generation
        const generationData = {
          courseId: courseId,
          themeId: activeThemeId,
          level: values.level,
          model: values.model,
          customPrompt: values.additionalPrompt || undefined
        };
        
        const response = await coursesAPI.generateThemeBlocks(generationData);
        
        setSnackbar({
          open: true,
          message: `Successfully generated ${response.data?.blocks?.length || 0} content blocks`,
          severity: 'success'
        });
      }
      
      // Refresh blocks after generation
      const blocksResponse = await coursesAPI.getBlocksForTheme(activeThemeId);
      setBlocks(Array.isArray(blocksResponse.data) 
        ? blocksResponse.data.sort((a, b) => a.order - b.order)
        : []);
        
      handleCloseGenerationDialog();
    } catch (error) {
      console.error('Error generating content:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate content blocks',
        severity: 'error'
      });
    } finally {
      setGeneratingLevel(null);
      setIsGenerating(false);
      setSubmitting(false);
    }
  };

  const handleOpenAudioDialog = (block) => {
    setSelectedBlock(block);
    setAudioDialogOpen(true);
  };

  const handleCloseAudioDialog = () => {
    setAudioDialogOpen(false);
    setSelectedBlock(null);
  };

  const handleGenerateAudio = async (values) => {
    if (!selectedBlock) return;

    try {
      if (values.endpoint === 'openai-longform') {
        await coursesAPI.generateBlockAudioOpenAI({
          blockId: selectedBlock.id,
          voice: values.voice,
          model: values.model,
          instructions: values.instructions || undefined
        });
      } else {
        await coursesAPI.generateBlockAudioGoogle({
          blockId: selectedBlock.id,
          voiceName: values.voiceName,
          languageCode: values.languageCode,
          useSsml: values.isSsml
          // audioEncoding parameter removed to use API default (MP3)
        });
      }

      setSnackbar({
        open: true,
        message: 'Audio generation job started successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error generating audio:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate audio: ' + (error.response?.data?.message || 'Unknown error'),
        severity: 'error'
      });
    }
    
    handleCloseAudioDialog();
  };

  // Add handler for deleting block audio
  const handleDeleteBlockAudio = async (blockId) => {
    if (!window.confirm('Are you sure you want to delete the audio for this block?')) {
      return;
    }
    
    try {
      await coursesAPI.deleteBlockAudio(blockId);
      
      // Update the blocks list by removing the audioUrl from the specific block
      setBlocks(blocks.map(block => 
        block.id === blockId ? { ...block, audioUrl: null } : block
      ));
      
      setSnackbar({
        open: true,
        message: 'Audio file deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting audio:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete audio file: ' + (error.response?.data?.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };

  const handleOpenBulkAudioDialog = () => {
    // Get the total number of blocks across all levels
    const allBlocks = Object.values(blocksByLevel).flat();
    setTotalBlocksForAudio(allBlocks.length);
    setBulkAudioDialogOpen(true);
  };

  const handleGenerateBulkAudio = async (values) => {
    if (!activeThemeId) return;
    
    setIsGeneratingBulkAudio(true);
    setBulkAudioProgress(0);
    
    try {
      // Get all blocks in the theme
      const allBlocks = Object.values(blocksByLevel).flat();
      let successCount = 0;
      let errorCount = 0;
      
      // Process each block sequentially
      for (let i = 0; i < allBlocks.length; i++) {
        const block = allBlocks[i];
        
        try {
          if (values.endpoint === 'openai-longform') {
            await coursesAPI.generateBlockAudioOpenAI({
              blockId: block.id,
              voice: values.voice,
              model: values.model,
              instructions: values.instructions || undefined
            });
          } else {
            // Ensure correct parameters for Google TTS
            await coursesAPI.generateBlockAudioGoogle({
              blockId: block.id, // Use the actual block ID, not "bulk"
              voiceName: values.voiceName,
              languageCode: values.languageCode,
              useSsml: values.isSsml || false
              // Don't include audioEncoding parameter if it's not expected by the API
            });
          }
          
          successCount++;
        } catch (error) {
          console.error(`Error generating audio for block ${block.id}:`, error);
          errorCount++;
        }
        
        // Update progress
        setBulkAudioProgress(i + 1);
        
        // Add a small delay between requests to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Show success message
      setSnackbar({
        open: true,
        message: `Started audio generation for ${successCount} blocks${errorCount > 0 ? ` (${errorCount} failed)` : ''}`,
        severity: errorCount > 0 ? 'warning' : 'success'
      });
    } catch (error) {
      console.error('Error in bulk audio generation:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate audio for blocks',
        severity: 'error'
      });
    } finally {
      setIsGeneratingBulkAudio(false);
      setBulkAudioDialogOpen(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Group blocks by level
  const blocksByLevel = blocks.reduce((acc, block) => {
    if (!acc[block.level]) {
      acc[block.level] = [];
    }
    acc[block.level].push(block);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/courses')}
          sx={{ mb: 2 }}
        >
          Back to Courses
        </Button>
        
        {course && (
          <>
            {/* Course header */}
            <CourseHeader 
              course={course} 
              onEditClick={handleCourseDialogOpen} 
            />
            
            {/* Course details with cover generation */}
            <CourseDetailsPanel 
              course={course} 
              onGenerateCover={handleGenerateCover}
              generatingCovers={generatingCovers}
            />
            
            {/* Tabs navigation */}
            <Box sx={{ mb: 3 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Themes" />
                <Tab label="Content Blocks" />
              </Tabs>
            </Box>
            
            {/* Themes panel with generate capability */}
            {activeTab === 0 && (
              <ThemesPanel 
                themes={themes}
                activeThemeId={activeThemeId}
                onThemeAdd={() => handleOpenThemeDialog()}
                onThemeEdit={handleOpenThemeDialog}
                onThemeDelete={handleDeleteTheme}
                onThemeSelect={(themeId) => setActiveThemeId(themeId)}
                onThemeReorder={handleThemeReorder}
                onThemeGenerate={handleThemeContentGeneration} // Add this new prop
                themeStats={themeStats} // Pass stats to ThemesPanel
                onViewBlocks={handleViewThemeBlocks} // Add this new prop
              />
            )}
            
            {/* Blocks panel */}
            {activeTab === 1 && (
              <BlocksPanel
                activeThemeId={activeThemeId}
                themes={themes}
                blocksByLevel={blocksByLevel}
                onBlockAdd={() => handleOpenBlockDialog()}
                onBlockEdit={handleOpenBlockDialog}
                onBlockDelete={handleDeleteBlock}
                onBlockReorder={handleBlockReorder}
                onGenerateContent={handleOpenGenerationDialog}
                onGenerateAudio={handleOpenAudioDialog} // Add this prop
                onDeleteAudio={handleDeleteBlockAudio} // Add this prop
                onGenerateAllAudio={handleOpenBulkAudioDialog} // Add this new prop
              />
            )}
          </>
        )}
      </Box>

      {/* Dialogs */}
      <CourseFormDialog
        open={courseDialogOpen}
        course={course}
        onClose={handleCourseDialogClose}
        onSubmit={handleCourseSubmit}
      />
      
      <ThemeFormDialog
        open={themeDialogOpen}
        theme={editingTheme}
        onClose={handleCloseThemeDialog}
        onSubmit={handleThemeSubmit}
      />
      
      <BlockFormDialog
        open={blockDialogOpen}
        block={editingBlock}
        onClose={handleCloseBlockDialog}
        onSubmit={handleBlockSubmit}
      />

      {/* Content generation dialog */}
      <ContentGenerationDialog
        open={generationDialogOpen}
        onClose={handleCloseGenerationDialog}
        onSubmit={handleGenerateContent}
        courseName={course?.title || ''}
        themeName={themes.find(t => t.id === activeThemeId)?.title || ''}
        isGenerating={isGenerating}
        generatingLevel={generatingLevel} // Pass the generatingLevel state
      />

      {/* Audio generation dialog */}
      <AudioGenerationDialog
        open={audioDialogOpen}
        onClose={handleCloseAudioDialog}
        selectedText={{ 
          id: selectedBlock?.id, 
          type: 'block',  // Pass a type to distinguish blocks from book text
          title: selectedBlock?.title, // Pass block title for display
          content: selectedBlock?.content // Pass content for future reference
        }}
        onSuccess={handleGenerateAudio}
        onError={(error) => {
          setSnackbar({
            open: true,
            message: 'Failed to generate audio: ' + (error.message || 'Unknown error'),
            severity: 'error'
          });
          handleCloseAudioDialog();
        }}
      />

      {/* Bulk Audio Generation Dialog */}
      <AudioGenerationDialog
        open={bulkAudioDialogOpen}
        onClose={() => setBulkAudioDialogOpen(false)}
        selectedText={{ 
          id: 'bulk',
          type: 'bulk', 
          title: `Generate Audio for All Blocks`,
          content: `This will generate audio for all ${totalBlocksForAudio} blocks in the theme "${themes.find(t => t.id === activeThemeId)?.title || 'current theme'}"`
        }}
        onSuccess={handleGenerateBulkAudio}
        onError={(error) => {
          setSnackbar({
            open: true,
            message: 'Failed to generate audio: ' + (error.message || 'Unknown error'),
            severity: 'error'
          });
          setBulkAudioDialogOpen(false);
        }}
        bulkProgress={isGeneratingBulkAudio ? {
          current: bulkAudioProgress,
          total: totalBlocksForAudio
        } : null}
      />

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
